import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { CreateEmployeeDto, ImportEmployeeResult, employeesService } from '../../services/api/employees';
import { useToast } from '../../contexts/ToastContext';

interface EmployeeImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedEmployee {
  employeeCode: string;
  fullName: string;
  email: string;
  departmentId: number;
  managerId?: number;
  joinDate: string;
  status?: string;
}

const EmployeeImportModal: React.FC<EmployeeImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedEmployee[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportEmployeeResult | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setImportResult(null);

    try {
      const content = await file.text();
      const parsed = parseCSV(content);
      setParsedData(parsed);
    } catch (err: any) {
      showError('Failed to parse file: ' + err.message);
      setParsedData([]);
    }
  };

  const parseCSV = (content: string): ParsedEmployee[] => {
    const lines = content.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('File is empty or has no data rows');
    }

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    
    // Map header names to expected fields
    const headerMap: Record<string, string> = {
      'employee code': 'employeeCode',
      'employeecode': 'employeeCode',
      'code': 'employeeCode',
      'full name': 'fullName',
      'fullname': 'fullName',
      'name': 'fullName',
      'email': 'email',
      'department id': 'departmentId',
      'departmentid': 'departmentId',
      'department': 'departmentId',
      'manager id': 'managerId',
      'managerid': 'managerId',
      'manager': 'managerId',
      'join date': 'joinDate',
      'joindate': 'joinDate',
      'status': 'status',
    };

    const fieldIndices: Record<string, number> = {};
    headers.forEach((header, index) => {
      const field = headerMap[header];
      if (field) {
        fieldIndices[field] = index;
      }
    });

    // Check required fields
    const requiredFields = ['employeeCode', 'fullName', 'email', 'departmentId', 'joinDate'];
    const missingFields = requiredFields.filter((f) => fieldIndices[f] === undefined);
    if (missingFields.length > 0) {
      throw new Error(`Missing required columns: ${missingFields.join(', ')}`);
    }

    const employees: ParsedEmployee[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0 || values.every((v) => !v.trim())) continue;

      const employee: ParsedEmployee = {
        employeeCode: values[fieldIndices.employeeCode]?.trim() || '',
        fullName: values[fieldIndices.fullName]?.trim() || '',
        email: values[fieldIndices.email]?.trim() || '',
        departmentId: parseInt(values[fieldIndices.departmentId]?.trim(), 10) || 0,
        joinDate: values[fieldIndices.joinDate]?.trim() || '',
        status: values[fieldIndices.status]?.trim().toUpperCase() || 'ACTIVE',
      };

      if (fieldIndices.managerId !== undefined) {
        const managerId = parseInt(values[fieldIndices.managerId]?.trim(), 10);
        if (!isNaN(managerId) && managerId > 0) {
          employee.managerId = managerId;
        }
      }

      employees.push(employee);
    }

    return employees;
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      showError('No data to import');
      return;
    }

    setImporting(true);
    try {
      const employees: CreateEmployeeDto[] = parsedData.map((emp) => ({
        employeeCode: emp.employeeCode,
        fullName: emp.fullName,
        email: emp.email,
        departmentId: emp.departmentId,
        managerId: emp.managerId,
        joinDate: emp.joinDate,
        status: (emp.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as any,
      }));

      const result = await employeesService.import(employees);
      setImportResult(result);

      if (result.success > 0) {
        showSuccess(`Successfully imported ${result.success} employee(s)`);
        if (result.failed === 0) {
          onSuccess();
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      }
      if (result.failed > 0) {
        showError(`${result.failed} employee(s) failed to import`);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Import failed';
      showError(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setParsedData([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const headers = ['Employee Code', 'Full Name', 'Email', 'Department ID', 'Manager ID', 'Join Date', 'Status'];
    const sampleData = [
      ['EMP001', 'John Doe', 'john.doe@company.com', '1', '', '2024-01-15', 'ACTIVE'],
      ['EMP002', 'Jane Smith', 'jane.smith@company.com', '2', '1', '2024-02-01', 'ACTIVE'],
    ];

    const csvContent = [headers.join(','), ...sampleData.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employee_import_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('employees.import', 'Import Employees')}
      size="large"
    >
      <div className="import-section">
        <h3>{t('employees.importInstructions', 'Instructions')}</h3>
        <p>
          Upload a CSV file with employee data. Required columns: Employee Code, Full Name, Email,
          Department ID, Join Date. Optional: Manager ID, Status.
        </p>
        <div className="template-download">
          <button type="button" className="button-link" onClick={downloadTemplate}>
            📥 Download CSV Template
          </button>
        </div>
      </div>

      <div className="import-section">
        <h3>{t('employees.selectFile', 'Select File')}</h3>
        <div className="file-input-wrapper">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileSelect}
            disabled={importing}
          />
          <div className="file-input-label">
            📁 {t('employees.chooseFile', 'Choose CSV file...')}
          </div>
        </div>
        {selectedFile && (
          <div className="selected-file">
            Selected: {selectedFile.name} ({parsedData.length} rows)
          </div>
        )}
      </div>

      {parsedData.length > 0 && (
        <div className="import-section">
          <h3>{t('employees.preview', 'Preview')} ({parsedData.length} employees)</h3>
          <div className="import-preview">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('employees.employeeCode')}</th>
                  <th>{t('employees.fullName')}</th>
                  <th>{t('employees.email')}</th>
                  <th>{t('employees.department')}</th>
                  <th>{t('employees.manager')}</th>
                  <th>{t('employees.joinDate')}</th>
                  <th>{t('employees.status')}</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, 10).map((emp, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{emp.employeeCode}</td>
                    <td>{emp.fullName}</td>
                    <td>{emp.email}</td>
                    <td>{emp.departmentId}</td>
                    <td>{emp.managerId || '-'}</td>
                    <td>{emp.joinDate}</td>
                    <td>{emp.status}</td>
                  </tr>
                ))}
                {parsedData.length > 10 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', fontStyle: 'italic' }}>
                      ... and {parsedData.length - 10} more rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importResult && (
        <div
          className={`import-results ${
            importResult.failed === 0
              ? 'success'
              : importResult.success > 0
              ? 'warning'
              : 'error'
          }`}
        >
          <h4>{t('employees.importResults', 'Import Results')}</h4>
          <p>✓ Successfully imported: {importResult.success}</p>
          <p>✗ Failed: {importResult.failed}</p>
          {importResult.errors.length > 0 && (
            <div className="import-errors">
              {importResult.errors.map((error, index) => (
                <div key={index} className="import-error-item">
                  Row {error.row}: {error.employeeCode ? `[${error.employeeCode}] ` : ''}{error.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="button button-secondary" onClick={handleClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button-success"
          onClick={handleImport}
          disabled={parsedData.length === 0 || importing}
        >
          {importing ? t('common.loading') : t('employees.importButton', 'Import Employees')}
        </button>
      </div>
    </Modal>
  );
};

export default EmployeeImportModal;
